import { t } from "@lingui/macro";
import { useState } from "react";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import {
  BellIcon,
  CaretCircleLeftIcon,
  CaretCircleRightIcon,
  ExportIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  TagIcon,
  CoinIcon,
  PencilSimpleIcon,
  TrashIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import { useRates } from "@/client/services/rate/rates";
import { useFilterRate } from "@/client/stores/filter";
import { useDialog } from "@/client/stores/dialog";
import { useExportRate } from "@/client/services/rate/export-rate";
import qs from "qs";
import { Column, DataTable } from "../_components/datatables";
import { CardContent, CardRoot, TitleCard } from "../_components/card";

export const RateAdminPage = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const { filter, replaceFilter, resetFilter } = useFilterRate();
  const diologCreate = useDialog("create-rate");
  const { open } = useDialog("filter-rate");
  const { rates, error, isLoading } = useRates(page, filter);
  const { refetch } = useExportRate(qs.stringify(filter));

  const handleBackpage = () => setPage(page - 1);
  const handleForwardpage = () => setPage(page + 1);
  const handleSpecficpage = (newpage: number) => setPage(newpage);

  const applyQuickFilters = () => {
    const nextFilter = { ...filter };
    delete nextFilter.name;

    if (search.trim()) {
      nextFilter.name = { contains: search.trim() };
    }

    setPage(1);
    replaceFilter(nextFilter);
  };

  const resetFilters = () => {
    setSearch("");
    setPage(1);
    resetFilter();
  };

  // Colonnes basées sur votre RateTable : Name, Price, Credit, Actions
  const columns: Column<any>[] = [
    {
      key: "name",
      header: "Name",
      render: (item) => <span className="font-medium text-admin-text-primary">{item.name}</span>,
    },
    {
      key: "price",
      header: "Price",
      render: (item) => <span className="text-admin-text-secondary">{item.price}</span>,
    },
    {
      key: "credit",
      header: "Credit",
      render: (item) => <span className="text-admin-text-secondary">{item.credit}</span>,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (item) => (
        <div className="flex justify-end gap-2 text-admin-text-secondary">
          {/* Bouton Modifier */}
          <Button
            variant="ghost"
            size="icon"
            aria-label={t`Modifier le tarif`}
            className="hover:text-admin-text-primary"
            onClick={() => {
              open("create", { id: "create-rate", item: { id: item.id } });
            }}
          >
            <PencilSimpleIcon size={18} />
          </Button>

          {/* Bouton Supprimer */}
          <Button
            variant="ghost"
            size="icon"
            aria-label={t`Supprimer le tarif`}
            className="hover:text-admin-danger"
          >
            <TrashIcon size={18} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Header de page */}
      <div className="mb-8 flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <h1 className="text-3xl font-bold text-admin-text-primary">Tarifs</h1>
          <p className="mt-1 text-sm text-admin-text-muted">{t`Tableau de bord / Tarifs`}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative hidden md:block">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder={t`Rechercher des tarifs…`}
              className="h-10 rounded-full bg-admin-surface pl-10 pr-4 text-admin-text-primary"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            aria-label="Notifications"
            className="rounded-full bg-admin-surface text-admin-text-secondary hover:text-admin-text-primary"
          >
            <BellIcon size={18} />
          </Button>
          <div className="h-8 w-8 overflow-hidden rounded-full bg-admin-primary">
            <img src="/avatar.jpg" alt={t`Utilisateur`} className="h-full w-full object-cover" />
          </div>
        </div>
      </div>

      {/* Cartes de Statistiques */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <CardRoot>
          <CardContent>
            <TitleCard
              text="Tarifs"
              number={rates?.data?.length.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-surface-elevated p-3 text-admin-text-secondary">
              <TagIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 4.2%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Avec crédits"
              number={rates?.data?.filter((rate) => rate.credit > 0).length.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-success/20 p-3 text-admin-success">
              <CoinIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-success">▲ 3.7%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Page actuelle"
              number={page.toString()}
              classNameText=""
              classNameNumber=""
            />
            <div className="rounded-full bg-admin-accent/20 p-3 text-admin-accent">
              <CoinIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-xs text-admin-accent">● Live</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`mis à jour`}</span>
          </div>
        </CardRoot>

        <CardRoot>
          <CardContent>
            <TitleCard
              text="Pages totales"
              number={rates?.page?.toString() ?? "0"}
              classNameText=""
              classNameNumber=""
            />
            <div className="bg-admin-warning/20 text-admin-warning rounded-full p-3">
              <TagIcon size={24} weight="duotone" />
            </div>
          </CardContent>
          <div className="px-5 pb-5">
            <span className="text-admin-warning text-xs">▲ 1.3%</span>{" "}
            <span className="text-xs text-admin-text-muted">{t`par rapport au mois dernier`}</span>
          </div>
        </CardRoot>
      </div>

      {/* Section Filtres et Actions */}
      <div className="mb-8 rounded-xl-admin border border-admin-border bg-admin-surface p-6">
        <div className="flex flex-col flex-wrap justify-between gap-4 lg:flex-row">
          {/* Recherche */}
          <div className="relative min-w-[250px] flex-1">
            <MagnifyingGlassIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-admin-text-muted"
              size={18}
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder={t`Rechercher par nom…`}
              className="h-10 w-full bg-admin-background pl-10 text-admin-text-primary"
            />
          </div>

          <div className="flex items-end gap-3">
            <Button
              variant="outline"
              onClick={applyQuickFilters}
              className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
            >
              <FunnelIcon size={16} className="mr-2" /> Filter
            </Button>
            <Button
              variant="outline"
              onClick={() => open("create", { id: "filter-rate" })}
              className="h-10 border-admin-border bg-admin-background text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text-primary"
            >
              <FunnelIcon size={16} className="mr-2" /> Advanced filters
            </Button>
            <Button
              variant="ghost"
              onClick={resetFilters}
              className="h-10 text-admin-text-muted hover:text-admin-text-primary"
            >
              Reset
            </Button>
            <Button
              variant="outline"
              onClick={() => refetch()}
              className="h-10 bg-admin-background text-admin-text-secondary hover:text-admin-text-primary"
            >
              <ExportIcon size={16} className="mr-2" /> Export
            </Button>
            <Button
              onClick={() => diologCreate.open("create", { id: "create-rate" })}
              className="h-10 bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90"
            >
              <PlusIcon size={16} className="mr-2" />
              {t`Nouveau tarif`}
            </Button>
          </div>
        </div>
      </div>

      {/* Tableau & Pagination */}
      <DataTable
        title=""
        data={rates?.data ?? []}
        columns={columns}
        pageSize={10}
        className="shadow-sm-admin"
      />

      {/* Pagination (Style UserAdminPage) */}
      <div className="my-3 flex flex-row justify-end space-x-3">
        {page > 1 && (
          <Button variant="outline" onClick={handleBackpage} className="rounded-lg">
            <CaretCircleLeftIcon size={32} weight="regular" />
          </Button>
        )}
        {page - 2 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page - 2)}
            className="rounded-lg"
          >
            {page - 2}
          </Button>
        )}
        {page - 1 > 0 && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page - 1)}
            className="rounded-lg"
          >
            {page - 1}
          </Button>
        )}

        <Button variant="secondary" className="bg-admin-primary text-admin-primary-foreground">
          {page}
        </Button>
        {rates && page + 1 <= rates.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page + 1)}
            className="rounded-lg"
          >
            {page + 1}
          </Button>
        )}
        {rates && page + 2 <= rates.page && (
          <Button
            variant="outline"
            onClick={() => handleSpecficpage(page + 2)}
            className="rounded-lg"
          >
            {page + 2}
          </Button>
        )}
        {rates && page < rates.page && (
          <Button variant="outline" onClick={handleForwardpage} className="rounded-lg">
            <CaretCircleRightIcon size={32} weight="regular" />
          </Button>
        )}
      </div>
    </>
  );
};
